import i18n from '@/i18n/index.js'
import { defaultLocale } from '@/config/flavor'
import {
  isImportFailReason,
  normalizeImportFailReasonText,
  translateImportFailReason,
} from '@/utils/importFailReasonI18n.js'

function shouldTranslateImportFailReasons () {
  const locale = i18n.locale || defaultLocale
  return locale !== 'zh' && !locale.startsWith('zh-')
}

function getCellText (cell) {
  if (!cell) return null
  if (typeof cell.v === 'string') return cell.v
  if (typeof cell.w === 'string') return cell.w
  if (cell.v != null) return String(cell.v)
  return null
}

async function toExcelArrayBuffer (data) {
  if (data instanceof ArrayBuffer) {
    return data
  }
  if (typeof Uint8Array !== 'undefined' && data instanceof Uint8Array) {
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return data.arrayBuffer()
  }
  return data
}

function hideWorkbookComments (workbook) {
  workbook.SheetNames.forEach((sheetName) => {
    const sheet = workbook.Sheets[sheetName]
    if (!sheet) return
    Object.keys(sheet).forEach((cellAddr) => {
      if (cellAddr[0] === '!') return
      const cell = sheet[cellAddr]
      if (cell && cell.c) {
        // SheetJS write_comments_vml 读取 cell.c（数组）上的 hidden，而非单个 comment 项
        cell.c.hidden = true
      }
    })
  })
}

/**
 * 仅在非中文环境下，将导入失败报告 Excel 中的失败原因翻译为当前语言。
 * 解析失败或文件不含失败原因时，原样返回传入的数据。
 */
export async function translateImportFailExcelIfNeeded (data) {
  if (!shouldTranslateImportFailReasons()) {
    return data
  }

  try {
    const XLSX = await import(/* webpackChunkName: "xlsx" */ 'xlsx')
    const arrayBuffer = await toExcelArrayBuffer(data)
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellComments: true })

    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName]
      Object.keys(sheet).forEach((cellAddr) => {
        if (cellAddr[0] === '!') return

        const cell = sheet[cellAddr]
        const text = getCellText(cell)
        if (!text) return

        const normalizedText = normalizeImportFailReasonText(text)
        if (!normalizedText || !isImportFailReason(normalizedText)) {
          return
        }

        const translated = translateImportFailReason(normalizedText)
        if (translated !== normalizedText) {
          cell.v = translated
          cell.w = translated
        }
      })
    })

    hideWorkbookComments(workbook)
    return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
  } catch (error) {
    console.warn('Import fail reason Excel translation skipped:', error)
    return data
  }
}
